import React, { useEffect, useState } from 'react';
import ProductDetailContent from './ProductDetailContent';
import BackOrderAdjustModal from '../backorder/BackOrderAdjustModal';
import { useProduct } from '../../hooks/useProducts';
import { useBackOrder } from '../../hooks/useBackOrder';
import { useAuth } from '../../hooks/useAuth';
import { useCart } from '../../hooks/useCart';
import { isBelowOneCase } from '../../utils/quantityHelpers';

const ProductDetailModal = ({ productId, isOpen, onClose, onOpenFullPage }) => {
  // Back-order button state (rendered in the footer). useProduct shares the react-query cache
  // with ProductDetailContent, so this adds no extra fetch.
  const { data: product } = useProduct(productId);
  const { isEmployee } = useAuth();
  const { getItemBackOrderQuantity, canUseBackOrder } = useBackOrder();
  const { getItemQuantityInCart } = useCart();
  const [showBackOrderModal, setShowBackOrderModal] = useState(false);

  // Only offer a back order once less than a full case is left to buy (available minus cart).
  const canCreateBackOrder =
    !isEmployee() && product?.allowBackorder &&
    isBelowOneCase(product, product ? getItemQuantityInCart(product.id) : 0);
  const currentBackOrderQty = getItemBackOrderQuantity(product?.id);
  const showBackOrderButton = canUseBackOrder && (canCreateBackOrder || currentBackOrderQty > 0);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !productId) {
    return null;
  }

  return (
    <div
      className="modal show d-block product-detail-modal"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content rounded-0">
          {/* Modal Header */}
          <div className="modal-header border-bottom justify-content-between">
            <h5 className="modal-title">
              <i className="bi bi-info-circle me-2"></i>
              Product Details
            </h5>
            <div className="d-flex gap-2 align-items-center">
              {/* Full Page Button */}
              {onOpenFullPage && (
                <button 
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={() => onOpenFullPage(productId)}
                  title="Open in full page"
                  aria-label="Open in full page"
                >
                  <i className="bi bi-arrows-fullscreen"></i>
                </button>
              )}
              
              {/* Close Button */}
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                aria-label="Close"
              ></button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="modal-body p-4">
            <ProductDetailContent 
              productId={productId} 
              isModal={true}
              onClose={onClose}
            />
          </div>

          {/* Modal Footer */}
          <div className="modal-footer border-top bg-light">
            <div className="d-flex justify-content-between w-100">
              <div className="d-flex align-items-center">
                <small className="text-muted">
                  <i className="bi bi-lightbulb me-1"></i>
                  Tip: Use arrow keys to navigate between images
                </small>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Continue Shopping
                </button>
                {showBackOrderButton && (
                  <button
                    type="button"
                    className="btn btn-outline-dark"
                    onClick={() => setShowBackOrderModal(true)}
                  >
                    <i className="bi bi-clock-history me-2"></i>
                    {currentBackOrderQty > 0 ? "View Back Order" : "Create Back Order"}
                  </button>
                )}
                {onOpenFullPage && (
                  <button 
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={() => onOpenFullPage(productId)}
                  >
                    <i className="bi bi-arrows-fullscreen me-2"></i>
                    View Full Details
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <BackOrderAdjustModal
        show={showBackOrderModal}
        onHide={() => setShowBackOrderModal(false)}
        product={product}
      />

      <style>{`
        /* Scoped to .product-detail-modal so these rules don't leak to other .modal elements
           on the page (e.g. the back-order modal that portals to <body>). */
        .product-detail-modal {
          --bs-modal-width: 95%;
        }

        .product-detail-modal .modal-xl {
          max-width: 1200px;
        }

        .product-detail-modal .modal-dialog-scrollable {
          max-height: calc(100vh - 2rem);
        }

        .product-detail-modal .modal-content {
          max-height: 100%;
          border: none;
          border-radius: 0.5rem;
          box-shadow: 0 10px 30px rgba(0,0,0,0.2);
        }

        .product-detail-modal .modal-header {
          background-color: #f8f9fa;
          border-radius: 0.5rem 0.5rem 0 0;
        }

        .product-detail-modal .modal-body {
          flex: 1;
          overflow-y: auto;
          max-height: calc(100vh - 200px);
        }

        .product-detail-modal .modal-footer {
          border-radius: 0 0 0.5rem 0.5rem;
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .product-detail-modal {
            --bs-modal-width: 100%;
            --bs-modal-margin: 0;
          }

          .product-detail-modal .modal-dialog {
            margin: 0;
            max-width: 100%;
            height: 100vh;
          }

          .product-detail-modal .modal-content {
            height: 100vh;
            border-radius: 0;
          }

          .product-detail-modal .modal-header,
          .product-detail-modal .modal-footer {
            border-radius: 0;
          }

          .product-detail-modal .modal-body {
            max-height: none;
            flex: 1;
            padding: 1rem;
          }
        }

        /* Animation */
        .product-detail-modal.show {
          animation: productDetailModalFadeIn 0.2s ease-out;
        }

        @keyframes productDetailModalFadeIn {
          from {
            opacity: 0;
            transform: translateY(-50px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .product-detail-modal .modal-dialog {
          transition: transform 0.2s ease-out;
        }

        /* Scrollbar styling for modal body */
        .product-detail-modal .modal-body::-webkit-scrollbar {
          width: 6px;
        }

        .product-detail-modal .modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }

        .product-detail-modal .modal-body::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }

        .product-detail-modal .modal-body::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }

        /* Accessibility improvements */
        .product-detail-modal .btn-close:focus {
          box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .product-detail-modal .modal-content {
            border: 2px solid;
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          .product-detail-modal.show,
          .product-detail-modal .modal-dialog {
            animation: none;
            transition: none;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailModal;